import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Cloud, CloudOff } from "lucide-react";
import { SERVICE_COLORS, todayStr, isActiveOn } from "./crewUtils";
import { AssignmentForm } from "./AssignmentForm";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ date: d.toLocaleDateString("en-CA"), inMonth: d.getMonth() === month });
  }
  return cells;
}

export function CalendarModule({ assignments, onRefresh, readOnly = false }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(todayStr());
  const [formState, setFormState] = useState(null); // null | {initial}

  const nav = (dir) => {
    let m = month + dir, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };

  const cells = monthGrid(year, month);
  const today = todayStr();
  const dayAssignments = assignments.filter((a) => isActiveOn(a, selectedDay));

  return (
    <div className="space-y-6" data-testid="calendar-module">
      <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">{readOnly ? "My Schedule" : "Crew Calendar"}</div>
            <p className="mt-1 text-sm text-[#23282B]/60">{readOnly ? "Jobs you're assigned to. Check here each morning to see where you're headed." : "Schedule jobs and see who's booked where. Jobs sync automatically to Char's phone calendar (CalDAV)."}</p>
          </div>
          {!readOnly && (
          <button onClick={() => setFormState({ initial: { start_date: selectedDay, end_date: selectedDay } })} data-testid="calendar-new-assignment" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
            <Plus size={15} /> New Assignment
          </button>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="font-display text-xl md:text-2xl uppercase text-[#23282B]" data-testid="calendar-month-label">{MONTHS[month]} {year}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => nav(-1)} data-testid="calendar-prev" aria-label="Previous month" className="p-2 border border-[#23282B]/20 hover:border-[#2F5F80] hover:text-[#2F5F80] transition-colors"><ChevronLeft size={16} /></button>
            <button onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelectedDay(today); }} data-testid="calendar-today" className="px-3 py-2 text-xs font-bold tracking-[0.12em] uppercase border border-[#23282B]/20 hover:border-[#2F5F80] hover:text-[#2F5F80] transition-colors">Today</button>
            <button onClick={() => nav(1)} data-testid="calendar-next" aria-label="Next month" className="p-2 border border-[#23282B]/20 hover:border-[#2F5F80] hover:text-[#2F5F80] transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-7 gap-px bg-[#23282B]/10 border border-[#23282B]/10" data-testid="calendar-grid">
          {DOW.map((d) => (
            <div key={d} className="bg-[#23282B] text-white/80 text-[10px] font-bold tracking-[0.2em] uppercase text-center py-2">{d}</div>
          ))}
          {cells.map((c) => (
            <DayCell
              key={c.date}
              cell={c}
              items={assignments.filter((a) => isActiveOn(a, c.date))}
              isToday={c.date === today}
              isSelected={c.date === selectedDay}
              onSelect={setSelectedDay}
            />
          ))}
        </div>

        {/* Selected day detail */}
        <div className="mt-6 border-t border-[#23282B]/10 pt-5" data-testid="calendar-day-detail">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#2F5F80]">{selectedDay}</div>
          {dayAssignments.length === 0 ? (
            <p className="mt-2 text-sm text-[#23282B]/50">{readOnly ? "Nothing scheduled for you on this day." : 'Nothing scheduled. Click "New Assignment" to book this day.'}</p>
          ) : (
            <div className="mt-3 space-y-2">
              {dayAssignments.map((a) => (
                <DayDetailRow key={a.id} a={a} readOnly={readOnly} onEdit={(x) => setFormState({ initial: x })} />
              ))}
            </div>
          )}
        </div>
      </section>

      {formState && (
        <AssignmentForm initial={formState.initial} onClose={() => setFormState(null)} onSaved={onRefresh} />
      )}
    </div>
  );
}

function DayCell({ cell, items, isToday, isSelected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(cell.date)}
      data-testid={`calendar-day-${cell.date}`}
      className={`min-h-[86px] p-1.5 text-left align-top transition-colors ${cell.inMonth ? "bg-white" : "bg-[#F1F3F5]"} ${isSelected ? "outline outline-2 outline-[#2F5F80] -outline-offset-2" : "hover:bg-[#9CC4DB]/10"}`}
    >
      <span className={`inline-flex items-center justify-center text-xs font-semibold w-6 h-6 ${isToday ? "bg-[#2F5F80] text-white" : cell.inMonth ? "text-[#23282B]" : "text-[#23282B]/35"}`}>
        {Number(cell.date.slice(-2))}
      </span>
      <div className="mt-1 space-y-0.5">
        {items.slice(0, 3).map((a) => (
          <div key={a.id} className={`truncate text-[10px] font-semibold text-white px-1 py-0.5 ${a.status === "complete" ? "opacity-40 line-through" : ""}`} style={{ background: SERVICE_COLORS[a.service_type] || "#23282B" }}>
            {a.title}
          </div>
        ))}
        {items.length > 3 && <div className="text-[10px] text-[#23282B]/50 px-1">+{items.length - 3} more</div>}
      </div>
    </button>
  );
}

function DayDetailRow({ a, readOnly, onEdit }) {
  return (
    <button onClick={() => { if (!readOnly) onEdit(a); }} data-testid={`calendar-event-${a.id}`} className={`w-full text-left border-2 border-[#23282B]/10 p-3.5 transition-colors ${readOnly ? "cursor-default" : "hover:border-[#2F5F80]"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-2.5 h-2.5 shrink-0" style={{ background: SERVICE_COLORS[a.service_type] || "#23282B" }} />
        <span className="font-display text-lg uppercase text-[#23282B]">{a.title}</span>
        {a.status === "complete" && <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-2 py-0.5">Complete</span>}
        {a.caldav_synced ? (
          <span title="Synced to Char's calendar" data-testid={`caldav-synced-${a.id}`}><Cloud size={14} className="text-green-600" /></span>
        ) : (
          <span title="Not synced to phone calendar" data-testid={`caldav-unsynced-${a.id}`}><CloudOff size={14} className="text-[#23282B]/30" /></span>
        )}
        <span className="ml-auto text-xs text-[#23282B]/50">{a.start_date}{a.end_date !== a.start_date ? ` → ${a.end_date}` : ""}</span>
      </div>
      <div className="mt-1 text-xs text-[#23282B]/70">
        {a.location && <span className="mr-3">{a.location}</span>}
        {a.crew?.length > 0 && <span>Crew: {a.crew.join(", ")}</span>}
      </div>
    </button>
  );
}
